import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { COLLECTION_DEBT_TYPES } from "@constants";
import { DatePicker } from "components/datePicker";
import { Radio } from "components/radio";
import { useGetPaymentPortalConfigurationsQuery } from "features/lookupForm/lookupAPI";
import { Field, Form, Formik } from "formik";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

const VALIDATION_METHOD_FIELDS = {
  ssn: {
    name: "ssnLast4",
    label: "SSN Last 4 Digits",
    placeholder: "Enter last 4 digits of your SSN...",
    maxLength: 4,
  },
  dob: { name: "dob", label: "Date of Birth", component: "datePicker", placeholder: "MM/DD/YYYY" },
  phone: { name: "phoneNumber", label: "Phone", placeholder: "XXX-XXX-XXXX" },
  zip_code: { name: "zipCode", label: "Zip Code", placeholder: "Enter your zip code..." },
};

export default function LookupForm({ onSubmit, isLoading }) {
  const { slug } = useParams();
  const { data: paymentPortalConfigurations } = useGetPaymentPortalConfigurationsQuery({
    agencySlug: slug,
  });
  const lookupFormSchema = Yup.object().shape({
    collectionDebtType: Yup.string()
      .oneOf(COLLECTION_DEBT_TYPES.map((x) => x.value))
      .required("Required"),
    companyName: Yup.string()
      .min(1, "Too Short!")
      .max(64, "Too Long!")
      .when("collectionDebtType", {
        is: (value) => value === "commercial",
        then: (schema) => schema.required("Required"),
      }),
    companyEin: Yup.string().min(1, "Too Short!").max(64, "Too Long!"),
  });
  const collectionDebtTypeOptions = COLLECTION_DEBT_TYPES.map((debtType) => ({
    label: (
      <HStack>
        <Text>{debtType.label}</Text>
      </HStack>
    ),
    value: debtType.value,
  }));

  return (
    <Formik
      initialValues={{
        collectionDebtType: COLLECTION_DEBT_TYPES[0].value,
        firstName: "",
        lastName: "",
        externalAccountId: "",
        validationMethod: "",
        dob: undefined,
        ssnLast4: undefined,
        phoneNumber: undefined,
        companyName: "",
        companyEin: "",
      }}
      onSubmit={onSubmit}
      validationSchema={lookupFormSchema}
    >
      {(props) => (
        <Form>
          <VStack spacing={6}>
            <Field name="externalAccountId" required>
              {({ field, form }) => (
                <FormControl
                  isRequired
                  isInvalid={form.errors.externalAccountId && form.touched.externalAccountId}
                >
                  <FormLabel>Account Number</FormLabel>
                  <Input {...field} placeholder="Enter your account number..." />
                  <FormErrorMessage>{form.errors.externalAccountId}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="collectionDebtType" required>
              {({ field, form }) => (
                <FormControl
                  isRequired
                  isInvalid={form.errors.collectionDebtType && form.touched.collectionDebtType}
                >
                  <FormLabel>Debt Type</FormLabel>
                  <Radio form={form} field={field} options={collectionDebtTypeOptions} />
                  <FormErrorMessage>{form.errors.collectionDebtType}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            {props.values.collectionDebtType === "consumer" && (
              <>
                <Stack direction={["column", "column", "row"]} spacing={[0, 0, 6]} w="100%">
                  <Field name="firstName">
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        isInvalid={form.errors.firstName && form.touched.firstName}
                      >
                        <FormLabel>First Name</FormLabel>
                        <Input {...field} placeholder="Enter first name..." />
                        <FormErrorMessage>{form.errors.firstName}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="lastName">
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        isInvalid={form.errors.lastName && form.touched.lastName}
                        mt={[6, 6, 0]}
                      >
                        <FormLabel>Last Name</FormLabel>
                        <Input {...field} placeholder="Enter last name..." />
                        <FormErrorMessage>{form.errors.lastName}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Stack>
                <Field name="validationMethod">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.validationMethod && form.touched.validationMethod}
                    >
                      <FormLabel>Verification Method</FormLabel>
                      <Select
                        {...field}
                        placeholder="Select verification method..."
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          props.setFieldValue("validationMethod", selectedValue);

                          // Clear all validation fields except the selected one
                          const selectedField = VALIDATION_METHOD_FIELDS[selectedValue]?.name;
                          Object.values(VALIDATION_METHOD_FIELDS).forEach(({ name }) => {
                            if (name !== selectedField) {
                              props.setFieldValue(name, null);
                            }
                          });
                        }}
                      >
                        {paymentPortalConfigurations?.paymentVerificationMethods?.map((method) => (
                          <option key={method.value} value={method.value} label={method.display} />
                        ))}
                      </Select>
                      <FormErrorMessage>{form.errors.firstName}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                {VALIDATION_METHOD_FIELDS[props.values.validationMethod] &&
                  (() => {
                    const { name, label, placeholder, maxLength, component } =
                      VALIDATION_METHOD_FIELDS[props.values.validationMethod];
                    return (
                      <Field name={name}>
                        {({ field, form }) => (
                          <FormControl
                            isRequired
                            isInvalid={form.errors[name] && form.touched[name]}
                          >
                            <FormLabel>{label}</FormLabel>
                            {component === "datePicker" ? (
                              <DatePicker form={form} field={field} placeholderText={placeholder} />
                            ) : (
                              <Input {...field} maxLength={maxLength} placeholder={placeholder} />
                            )}
                            <FormErrorMessage>{form.errors[name]}</FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    );
                  })()}
              </>
            )}
            {props.values.collectionDebtType === "commercial" && (
              <>
                <Field name="companyName">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.companyName && form.touched.companyName}
                    >
                      <FormLabel>Company Name</FormLabel>
                      <Input {...field} placeholder="Enter company name..." />
                      <FormErrorMessage>{form.errors.companyName}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="companyEin">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.companyEin && form.touched.companyEin}
                      mt={[6, 6, 0]}
                    >
                      <FormLabel>Company EIN</FormLabel>
                      <Input {...field} placeholder="Enter company EIN..." />
                      <FormErrorMessage>{form.errors.companyEin}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </>
            )}
            {props.status && (
              <Alert status="error">
                <AlertIcon />
                {props.status}
              </Alert>
            )}
            <Button
              isLoading={props.isSubmitting || isLoading}
              colorScheme="blue"
              type="submit"
              w="180px"
              h="50px"
            >
              Next
            </Button>
            <Text mb={[8, 8, 4]} fontSize="sm" fontStyle="italic" color="gray.500">
              This communication is from a debt collector. This is an attempt to collect a debt. Any
              information obtained will be used for that purpose. Making a payment today does not
              overshadow the rights provided to you in your initial notice.
            </Text>
          </VStack>
        </Form>
      )}
    </Formik>
  );
}
